import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle, SectionSubtitle } from '../../styles/StyledComponents';

const ProcessSection = styled(Section)`
  padding: 100px 0;
  background-color: ${({ theme }) => theme.colors.lightBg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 80px 0;
  }
`;

const ProcessContainer = styled(Container)`
  max-width: 1200px;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ProcessCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }
`;

const ProcessIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  
  .material-icons {
    font-size: 2.5rem;
  }
`;

const ProcessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const ProcessDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text};
`;

const processSteps = [
  {
    icon: "explore",
    title: "Evaluate Where You Are",
    description: "We start by understanding your business, your workflows, and where the friction lives. No generic playbooks. Just sharp, focused discovery."
  },
  {
    icon: "psychology",
    title: "Make a Plan",
    description: "We design a strategy tailored to you: what to automate, what to build, and what to ignore. This isn't theory. It's a blueprint you can act on."
  },
  {
    icon: "auto_awesome",
    title: "Craft Your Brand",
    description: "Brand is how you earn trust. We help you shape your narrative so it aligns with your system and speaks to the right audience with confidence."
  },
  {
    icon: "architecture",
    title: "Design the System",
    description: "We architect systems that are simple, smart, and scalable. Whether it's an AI agent or a workflow engine, it's built to solve real problems."
  },
  {
    icon: "rocket_launch",
    title: "Implement the Software",
    description: "We don't hand off. We ship. We implement the stack from prototype to production and integrate it with what you already use."
  },
  {
    icon: "trending_up",
    title: "Scale + Secure",
    description: "As your system grows, we lock in performance and security. From compliance to observability, we make sure you're ready to scale with confidence."
  }
];

const Process = () => {
  return (
    <ProcessSection id="process">
      <ProcessContainer>
        <SectionTitle 
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          How We Work (For Real)
        </SectionTitle>
        <ProcessGrid>
          {processSteps.map((step, index) => (
            <ProcessCard
              key={index}
              as={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <ProcessIcon>
                <span className="material-icons">{step.icon}</span>
              </ProcessIcon>
              <ProcessTitle>{step.title}</ProcessTitle>
              <ProcessDescription>{step.description}</ProcessDescription>
            </ProcessCard>
          ))}
        </ProcessGrid>
      </ProcessContainer>
    </ProcessSection>
  );
};

export default Process;
